import React, { useState } from "react";
import { Button, Card, CoinBadge, EmptyState, Icon, Input, Skeleton, Tabs, fmtCoins } from "../components/ui";
import { BannerChars } from "../components/lux";
import CheckinWeek, { BONUS_EVERY } from "../components/coins/CheckinWeek";
import { useAsync } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import api from "../services/api";
import { IS_MOCK } from "../services/config";
import { errorInfo } from "../services/errors";
import "../styles/chat.css";
import "../styles/coins.css";

export default function Rewards() {
  const toast = useToast();
  const { setCoins } = useAuth();
  const [tab, setTab] = useState("checkin");
  const status = useAsync(() => api.rewards.checkinStatus(), []);
  const invite = useAsync(() => api.rewards.invite(), []);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [refCode, setRefCode] = useState("");
  const [refError, setRefError] = useState("");
  const [busy, setBusy] = useState(false);

  /** รันการรับรางวัล: สำเร็จ -> อัปเดตยอดคอยน์ทันที + รีโหลดสถานะ ; ผิดพลาด -> คืนข้อความจริง (return string) */
  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      if (typeof r?.tokensLeft === "number") setCoins(r.tokensLeft);
      toast(okMsg(r), "success");
      return { ok: true, r };
    } catch (e) {
      const info = errorInfo(e);
      toast(info.message, info.code === "already_done" ? "info" : "error");
      return { ok: false, message: info.message, code: info.code };
    } finally {
      setBusy(false);
    }
  };

  const claim = async () => {
    await act(api.rewards.checkin, (r) => `รับ ${fmtCoins(r.coins)} คอยน์แล้ว · เช็คอินต่อเนื่อง ${r.streak} วัน`);
    status.reload(); // สำเร็จหรือ already_done ก็โหลดสถานะใหม่ให้ตรงกับ server
  };
  const redeem = async (e) => {
    e.preventDefault();
    setCodeError("");
    const res = await act(() => api.rewards.redeem(code), (x) => `ได้รับ ${fmtCoins(x.coins)} คอยน์`);
    if (res.ok) setCode(""); else setCodeError(res.message);
  };
  const applyRef = async (e) => {
    e.preventDefault();
    setRefError("");
    const res = await act(() => api.rewards.applyReferral(refCode), (x) => `ได้รับ ${fmtCoins(x.coins)} คอยน์`);
    if (res.ok) { setRefCode(""); invite.reload(); } else setRefError(res.message);
  };
  const copy = (text, msg) => navigator.clipboard?.writeText(text).then(() => toast(msg, "success"), () => toast("คัดลอกไม่สำเร็จ", "error"));
  const share = async () => {
    const text = `มาคุยกับตัวละคร AI ที่ AITalk ด้วยกัน ใช้โค้ด ${invite.data.code} รับ ${invite.data.friendGets} คอยน์ฟรี`;
    if (navigator.share) { try { await navigator.share({ text }); return; } catch (e) { if (e?.name === "AbortError") return; } }
    copy(text, "คัดลอกข้อความชวนเพื่อนแล้ว");
  };

  const s = status.data;
  const cycle = s ? (s.streak > 0 && s.streak % BONUS_EVERY === 0 ? BONUS_EVERY : s.streak % BONUS_EVERY) : 0;
  return (
    <div className="rewards-page">
      <div className="page-head page-head--art"><div className="grow"><h1>รางวัล & ภารกิจ</h1><p>เช็คอินรายวัน กรอกโค้ด และชวนเพื่อนรับคอยน์</p></div><BannerChars ids={["233", "63", "280"]} /></div>
      <Tabs tabs={[{ value: "checkin", label: "เช็คอินรายวัน" }, { value: "code", label: "กรอกโค้ด" }, { value: "invite", label: "ชวนเพื่อน" }]} value={tab} onChange={setTab} />
      <div className="mt-16">
        {tab === "checkin" ? (
          <Card grad>
            {status.loading && !s ? <Skeleton height={220} /> : status.error ? (
              <div className="notice notice--error" role="alert"><span className="grow">{status.error}</span><Button size="sm" onClick={status.reload}>ลองใหม่</Button></div>
            ) : s ? (
              <>
                <div className="stats" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                  <div className="stat"><b>{s.streak || 0} วัน</b><span>สตรีคปัจจุบัน</span></div>
                  <div className="stat"><b>{s.longestStreak || 0} วัน</b><span>ติดต่อกันนานสุด</span></div>
                  <div className="stat"><b><CoinBadge amount={s.dailyCoins} /></b><span>รางวัลวันนี้{s.bonusToday ? ` (+${s.bonusToday} โบนัส)` : ""}</span></div>
                </div>
                <div className="row-between mt-16" style={{ fontSize: 13 }}>
                  <span>รับแล้ว <b>{cycle}</b> จาก {BONUS_EVERY} วัน</span>
                  <span className="muted">{s.canClaim ? "รับวันนี้ได้เลย" : "พรุ่งนี้มารับต่อ"}</span>
                </div>
                <div className="progress mt-8" role="progressbar" aria-label="ความคืบหน้าโบนัส" aria-valuemin={0} aria-valuemax={BONUS_EVERY} aria-valuenow={cycle}><i style={{ width: `${(cycle / BONUS_EVERY) * 100}%` }} /></div>
                <CheckinWeek status={s} />
                <Button variant="primary" size="lg" block disabled={busy || !s.canClaim} onClick={claim}>
                  {s.canClaim ? `รับรางวัลวันนี้ +${s.dailyCoins + (s.bonusToday || 0)} คอยน์` : "วันนี้รับแล้ว — พรุ่งนี้มาใหม่"}
                </Button>
                <div className="bonus-box mt-16"><Icon name="gift" size={18} /><span className="grow"><b>โบนัสพิเศษ</b> · เช็คอินต่อเนื่องทุก {BONUS_EVERY} วันรับโบนัสเพิ่ม (ระดับ Pro ขึ้นไปได้มากกว่า){s.nextBonusIn ? ` · อีก ${s.nextBonusIn} วัน` : ""}</span></div>
                <p className="faint mt-8" style={{ fontSize: 12 }}>เช็คอินรีเซ็ตทุกวันเวลา 00:00 น. (UTC+7) · ระดับคุยคลับที่สูงขึ้นได้คอยน์เช็คอินเพิ่ม</p>
              </>
            ) : null}
          </Card>
        ) : null}

        {tab === "code" ? (
          <Card>
            <form onSubmit={redeem} noValidate>
              <Input label="โค้ดจากอีเวนต์" placeholder="เช่น WELCOME50" value={code} onChange={(e) => { setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "")); setCodeError(""); }} error={codeError} hint="ตัวอักษร A-Z และตัวเลข" maxLength={40} autoComplete="off" />
              <Button type="submit" variant="primary" block disabled={busy || !code}>ใช้โค้ด</Button>
            </form>
            {IS_MOCK ? <p className="faint mt-16" style={{ fontSize: 12 }}>โหมดทดลอง: ลองโค้ด WELCOME50 หรือ AITALK100</p> : null}
          </Card>
        ) : null}

        {tab === "invite" ? (
          <Card>
            {invite.loading && !invite.data ? <Skeleton height={140} /> : invite.error ? (
              <div className="notice notice--error" role="alert"><span className="grow">{invite.error}</span><Button size="sm" onClick={invite.reload}>ลองใหม่</Button></div>
            ) : (
              <>
                <p className="muted">ชวนเพื่อนได้ {invite.data.perFriend} คอยน์ต่อคน (สูงสุด {invite.data.maxFriends} คน) เพื่อนที่กรอกโค้ดของคุณก็ได้ {invite.data.friendGets} คอยน์</p>
                <div className="card card--grad center mt-16">
                  <div className="faint" style={{ fontSize: 12 }}>โค้ดชวนเพื่อนของคุณ</div>
                  <div className="invite-code">{invite.data.code}</div>
                  <div className="row mt-8" style={{ justifyContent: "center", flexWrap: "wrap" }}>
                    <Button size="sm" onClick={() => copy(invite.data.code, "คัดลอกโค้ดแล้ว")}>คัดลอกโค้ด</Button>
                    <Button size="sm" variant="primary" onClick={share}>ส่งให้เพื่อน</Button>
                  </div>
                </div>
                <div className="stats mt-16" style={{ gridTemplateColumns: "1fr 1fr" }}>
                  <div className="stat"><b>{invite.data.invitedCount}/{invite.data.maxFriends}</b><span>เพื่อนที่ชวนแล้ว</span></div>
                  <div className="stat"><b><CoinBadge amount={invite.data.coinsEarned} /></b><span>คอยน์ที่ได้</span></div>
                </div>
                {invite.data.history.length ? (
                  <div className="mt-16">{invite.data.history.map((h, i) => (
                    <div key={h.inviteeId || i} className="hist__row"><div className="grow"><div className="strong">เพื่อนคนที่ {i + 1}</div><div className="faint" style={{ fontSize: 12 }}>{h.createdAt ? new Date(h.createdAt).toLocaleDateString("th-TH", { dateStyle: "medium" }) : ""}</div></div><CoinBadge>+{fmtCoins(h.coinsInviter)}</CoinBadge></div>
                  ))}</div>
                ) : <EmptyState icon="🤝" title="ยังไม่มีเพื่อนที่ใช้โค้ดของคุณ">ส่งโค้ดให้เพื่อนเพื่อรับคอยน์</EmptyState>}
                {!invite.data.referredBy ? (
                  <form className="mt-16" noValidate onSubmit={applyRef}>
                    <Input label="มีโค้ดจากเพื่อน?" value={refCode} onChange={(e) => { setRefCode(e.target.value.toUpperCase()); setRefError(""); }} error={refError} maxLength={20} hint="ใช้ได้ครั้งเดียวต่อบัญชี และใช้โค้ดของตัวเองไม่ได้" autoComplete="off" />
                    <Button type="submit" disabled={busy || !refCode}>ใช้โค้ดเพื่อน</Button>
                  </form>
                ) : <p className="faint mt-16" style={{ fontSize: 13 }}>คุณใช้โค้ดชวนเพื่อนไปแล้ว</p>}
              </>
            )}
          </Card>
        ) : null}
      </div>
    </div>
  );
}
